import { getEnvVariable } from '@/utils/get-env-variable';

const FORTER_SCRIPT_PATH = '/scripts/forter.js';
const FORTER_TOKEN_EVENT = 'ftr:tokenReady';
const FORTER_ID_TOKEN = getEnvVariable('VITE_FORTER_ID_TOKEN', '');

class ForterService {
  private forterToken: string = '';
  private isInitialized: boolean = false;
  private scriptElement: HTMLScriptElement | null = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!FORTER_ID_TOKEN) {
      console.warn('Forter: VITE_FORTER_ID_TOKEN not configured');
      return;
    }

    try {
      // Set up listener BEFORE loading script to catch the event
      this.setupTokenListener();
      await this.loadForterScript();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Forter:', error);
    }
  }

  private loadForterScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof document === 'undefined') {
        reject(new Error('Document is not available'));
        return;
      }

      const existingScript = document.getElementById('forter-script');
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'forter-script';
      script.src = FORTER_SCRIPT_PATH;
      script.setAttribute('data-siteId', FORTER_ID_TOKEN);
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Forter script'));

      document.head.appendChild(script);
      this.scriptElement = script;
    });
  }

  private setupTokenListener(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const eventListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      const token = customEvent.detail;

      if (token) {
        this.forterToken = token;
        console.log('Forter: Token received and stored');
      } else {
        console.warn('Forter: Token event fired but token is empty');
      }
    };

    document.addEventListener(FORTER_TOKEN_EVENT, eventListener);
    console.log('Forter: Event listener set up for', FORTER_TOKEN_EVENT);
  }

  getToken(): string {
    return this.forterToken;
  }

  isReady(): boolean {
    return this.isInitialized && this.forterToken !== '';
  }

  cleanup(): void {
    if (this.scriptElement && this.scriptElement.parentNode) {
      this.scriptElement.parentNode.removeChild(this.scriptElement);
      this.scriptElement = null;
    }
    this.forterToken = '';
    this.isInitialized = false;
  }
}

export const forterService = new ForterService();
